import React from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { useNavigate, Link } from 'react-router-dom';
// Import icons - Added FaCalendarAlt, FaClipboardList, FaChartBar for Chart of Accounts, FaWrench for Utilities, FaChartLine for Reports
import { FaUsers, FaUserTie, FaHome, FaTools, FaFileInvoiceDollar, FaCog, FaCalendarAlt, FaClipboardList, FaChartBar, FaWrench, FaChartLine, FaUserCircle, FaHistory, FaEnvelopeOpenText, FaBullhorn } from 'react-icons/fa';
import DashboardSwitch from '../components/DashboardSwitch';
import { terminology } from '../config/terminology'; // Import terminology
import { useAppConfig } from '../context/AppConfigContext'; // App config carries backend feature flags

// Define module data with separate Job entries
const modules = [
  { name: 'Franchisees', path: '/franchisees', icon: FaUserTie, color: 'bg-jims-cyan', hoverColor: 'hover:bg-jims-cyan-dark' },
  { name: terminology.client.plural, path: '/clients', icon: FaUsers, color: 'bg-jims-cyan-dark', hoverColor: 'hover:bg-jims-cyan' }, // Use terminology
  { name: terminology.household.plural, path: '/households', icon: FaHome, color: 'bg-jims-cyan', hoverColor: 'hover:bg-jims-cyan-dark' }, // Use terminology for Household
  // Separated Jobs into Scheduler and List
  { name: 'Job Scheduler', path: '/job-scheduler', icon: FaCalendarAlt, color: 'bg-jims-cyan-dark', hoverColor: 'hover:bg-jims-cyan' },
  { name: 'Jobs List', path: '/jobs-list', icon: FaClipboardList, color: 'bg-jims-cyan-dark', hoverColor: 'hover:bg-jims-cyan' },
  { name: 'Services', path: '/services', icon: FaTools, color: 'bg-jims-cyan', hoverColor: 'hover:bg-jims-cyan-dark' },
  { name: 'Invoices', path: '/invoices', icon: FaFileInvoiceDollar, color: 'bg-jims-cyan-dark', hoverColor: 'hover:bg-jims-cyan' },
  { name: 'Chart of Accounts', path: '/chart-of-accounts', icon: FaChartBar, color: 'bg-jims-cyan', hoverColor: 'hover:bg-jims-cyan-dark' },
  { name: 'Reports', path: '/reports', icon: FaChartLine, color: 'bg-jims-cyan-dark', hoverColor: 'hover:bg-jims-cyan' },
  { name: 'Utilities', path: '/utilities', icon: FaWrench, color: 'bg-jims-cyan-dark', hoverColor: 'hover:bg-jims-cyan' },
  { name: 'Account', path: '/account', icon: FaUserCircle, color: 'bg-jims-cyan', hoverColor: 'hover:bg-jims-cyan-dark' },
  // { name: 'Territories', path: '/territories', icon: FaMapMarkedAlt, color: 'bg-jims-cyan', hoverColor: 'hover:bg-jims-cyan-dark' },
  { name: 'Activity Log', path: '/activity-log', icon: FaHistory, color: 'bg-jims-cyan', hoverColor: 'hover:bg-jims-cyan-dark' },
  // Email Monitor is hidden unless the API's INBOX_MONITOR_ENABLED master switch is on (surfaced via app config).
  { name: 'Email Monitor', path: '/email-monitor', icon: FaEnvelopeOpenText, color: 'bg-jims-cyan-dark', hoverColor: 'hover:bg-jims-cyan', feature: 'inboxMonitor' },
  { name: 'Messaging', path: '/messaging', icon: FaBullhorn, color: 'bg-jims-cyan', hoverColor: 'hover:bg-jims-cyan-dark' },
  { name: 'Settings', path: '/settings', icon: FaCog, color: 'bg-gray-500', hoverColor: 'hover:bg-gray-600' }, // Keep settings gray
];

const Home = () => {
  const navigate = useNavigate();
  const { config } = useAppConfig();

  // Feature-flagged modules only show when their flag is on in the backend app config.
  const visibleModules = modules.filter((m) => m.feature !== 'inboxMonitor' || config?.inboxMonitorEnabled === true);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login'); // Redirect to login page after logout
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  return (
    <div className="container mx-auto px-4 py-12 md:py-16"> {/* Adjusted padding */}
      <div className="max-w-5xl mx-auto"> {/* Increased max-width */}
        <h1 className="text-4xl md:text-5xl font-bold text-center text-jims-cyan-dark mb-2"> {/* Use themed cyan */}
        Loope Admin Dashboard
        </h1>
        {/* Ticket 77: the way across to the new dashboard. Present on both sides, so neither is a trap. */}
        <div className="flex justify-center mb-8">
          <DashboardSwitch current="old" />
        </div>
        <div className="bg-white rounded-lg shadow-xl p-8 md:p-10"> {/* Increased padding/shadow */}
          <p className="text-xl text-center text-gray-700 mb-8"> {/* Added margin bottom */}
            Welcome, Admin! Select a module to manage:
          </p>

          {/* Module Links Grid - Now 10 items */}
          {/* Adjust grid columns to fit 10 modules better */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"> {/* Changed back to lg:grid-cols-4 */}
            {visibleModules.map((module) => (
              <Link
                key={module.name}
                to={module.path}
                className={`
                  ${module.color} ${module.hoverColor} text-white rounded-lg shadow-md
                  p-6 flex flex-col items-center justify-center text-center
                  transition duration-300 ease-in-out transform hover:-translate-y-1 hover:shadow-lg
                  ${module.name === 'Settings' ? 'md:col-start-2 lg:col-start-auto' : ''} // Adjust positioning for Settings if needed for 3 columns
                `}
              >
                <module.icon className="text-4xl mb-3" />
                <span className="font-semibold text-lg">{module.name}</span>
              </Link>
            ))}
          </div>

          {/* Logout Button - Centered */}
          <div className="mt-10 text-center"> {/* Added margin top and centered */}
            <button
              onClick={handleLogout}
              className="px-8 py-3 text-base font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition duration-300" // Increased padding/size
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
